import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { PropertyListings } from '@/types/PropertyListings';
import { Badge } from '@/components/ui/badge';
import Helper from '@/lib/helper';
import { Button } from '@/components/ui/button';
import { Bath, Bed, Car, MapIcon, MapPinPen, X, Clock } from 'lucide-react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/lib/redux/store';
import { GoogleMap, OverlayView } from '@react-google-maps/api';
import { useGoogleMaps } from '@/components/GoogleMapsProvider';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';

interface _CoShareListingTable {
    loading: boolean;
    listings: PropertyListings[] | null;
    getListings: (typeOverride?: string, pageOverride?: number, lengthOverride?: number, searchOverride?: string) => Promise<void>
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedListingId: React.Dispatch<React.SetStateAction<number | null>>;
    setActionType: React.Dispatch<React.SetStateAction<'delete' | 'deactivate' | 'close' | 'reopen' | 'activate' | 'repost'>>;
}


const defaultCenter = {
    lat: 3.139,
    lng: 101.6869
};

const CoShareListingTable : React.FC<_CoShareListingTable> = ({
    loading,
    listings,
    getListings,
    setOpen,
    setSelectedListingId,
    setActionType
}) => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [mapZoom, setMapZoom] = useState(12);
    const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
    const [initialBounds, setInitialBounds] = useState<google.maps.LatLngBounds | null>(null);
    const [selectedListing, setSelectedListing] = useState<PropertyListings | null>(null);
    const [activeTab, setActiveTab] = useState<'map' | 'list'>('list')
    const [mapType, setMapType] = useState<'roadmap' | 'satellite'>('satellite')
    const listViewRef = useRef<HTMLDivElement>(null)
    const isInitialLoadRef = useRef(true);
    const mapLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { isLoaded } = useGoogleMaps();
    
    const mapOptions = useMemo<google.maps.MapOptions | undefined>(() => {
        if (!isLoaded || !window.google) return undefined;
      
        return {
            disableDefaultUI: true,
            zoomControl: false,
            mapTypeControl: false,
            mapTypeId: mapType,
            styles: [
                { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                { featureType: 'transit', stylers: [{ visibility: 'off' }] },
                { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
            ],
        };
    }, [isLoaded, mapType]);

    const isPendingForAgent = (listing: PropertyListings) => {
        if (!listing.coShareListing) return false;
        if (listing.createdBy === agent.id) return false;
        if (!listing.sharedAgents || listing.sharedAgents.length === 0) return true;
        const agentInShared = listing.sharedAgents.find(
            sa => sa.agentId === agent.id && sa.type === 2
        );
        
        if (!agentInShared) return true;
        return agentInShared.status === 0;
    };

    const handleMarkerClick = (listing: PropertyListings) => {
        setSelectedListing(listing);
        if (mapInstance && listing.lat && listing.lng) {
            mapInstance.panTo({ lat: Number(listing.lat), lng: Number(listing.lng) });
            mapInstance.setZoom(16);
        }
    };

    const handleCardClick = (listing: PropertyListings) => {
        setSelectedListing(listing);
        if (mapInstance && listing.lat && listing.lng) {
            mapInstance.panTo({ lat: Number(listing.lat), lng: Number(listing.lng) });
            mapInstance.setZoom(16);
        }
        setActiveTab('map');
    };

    const handleResetView = () => {
        if (mapInstance && initialBounds) {
            mapInstance.fitBounds(initialBounds);
            setSelectedListing(null);
        }
    };

    const toggleMapType = () => {
        const newType = mapType === 'roadmap' ? 'satellite' : 'roadmap';
        setMapType(newType);
        if (mapInstance) {
            mapInstance.setMapTypeId(newType);
        }
    };

    const handleMapLoad = (map: google.maps.Map) => {
        console.log('Map loaded - setting initial load state');
        setMapInstance(map);
        isInitialLoadRef.current = true;
        
        if (listings && listings.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            listings.forEach((listing) => {
                if (listing.lat && listing.lng) {
                    bounds.extend(new google.maps.LatLng(Number(listing.lat), Number(listing.lng)));
                }
            });
            if (!bounds.isEmpty()) {
                setInitialBounds(bounds);
                map.fitBounds(bounds);
                const padding = { top: 50, right: 50, bottom: 50, left: 50 };
                map.fitBounds(bounds, padding);
            }
        }
        
        if (mapLoadTimeoutRef.current) {
            clearTimeout(mapLoadTimeoutRef.current);
        }
        
        mapLoadTimeoutRef.current = setTimeout(() => {
            isInitialLoadRef.current = false;
            console.log('Initial load complete - filtering enabled');
        }, 2000);
    };

    const handleCloseInfoWindow = () => setSelectedListing(null);


    useEffect(() => {
        if(mapInstance) {
            handleMapLoad(mapInstance);
        }
    }, [mapInstance, listings]);

    if (loading) {
        return (
            <div className='flex-1 mt-5 min-h-[90vh]'>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 pb-4">
                    {Array.from({ length: 12 }).map((_, index) => (
                        <div 
                            key={index}
                            className="bg-white overflow-hidden rounded-lg shadow-md"
                        >
                            {/* Image Skeleton */}
                            <Skeleton className="h-32 lg:h-36 w-full rounded-t-lg" />
                            
                            <div className="p-2.5 lg:p-3 space-y-3">
                                {/* Price Skeleton */}
                                <Skeleton className="h-5 w-28" />
                                
                                {/* Title Skeleton */}
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-4/5" />
                                
                                {/* Address Skeleton */}
                                <Skeleton className="h-3 w-full" />
                                
                                {/* Bed/Bath/Car Skeleton */}
                                <div className="flex gap-4">
                                    <Skeleton className="h-4 w-10" />
                                    <Skeleton className="h-4 w-10" />
                                    <Skeleton className="h-4 w-10" />
                                </div>
                                
                                {/* Agent + Button Skeleton */}
                                <div className="flex gap-3 items-center pt-2">
                                    <Skeleton className="h-10 w-10 rounded-full" />
                                    <div className="flex-1 space-y-2">
                                        <Skeleton className="h-4 w-32" />
                                        <Skeleton className="h-3 w-24" />
                                    </div>
                                    <Skeleton className="h-8 w-16" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className='flex-1 flex items-center justify-center min-h-[90vh]'>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading map...</p>
                </div>
            </div>
        );
    }
    
    return listings && (
        <div className='flex-1 flex flex-col lg:flex-row gap-4 overflow-hidden mt-5 min-h-[90vh]'>
            {/* <div className='relative md:w-1/2 rounded-lg overflow-hidden'>
                <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
                    <Button
                    className="bg-white hover:bg-gray-100 text-gray-700 shadow-lg text-start"
                    size="sm">
                        {listings?.length || 0} property found.
                    </Button>
                    {
                        selectedListing && (
                            <Button
                                onClick={handleResetView}
                                className="bg-white hover:bg-gray-100 text-gray-700 shadow-lg"
                                size="sm">
                                <MapIcon size={16} className="mr-2" />
                                Reset View
                            </Button>
                        )
                    }
                    <Button
                        onClick={toggleMapType}
                        className="bg-white hover:bg-gray-100 text-gray-700 shadow-lg text-start"
                        size="sm">
                        {
                            mapType === 'roadmap' ? (
                                <>
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Satellite
                                </>
                            ) : (
                                <>
                                    <MapIcon size={16} className="mr-2" />
                                    Map
                                </>
                            )
                        }
                    </Button>
                </div>
                <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={mapCenter}
                zoom={mapZoom}
                onLoad={handleMapLoad}
                options={mapOptions}>
                    {
                        listings.map(
                            (listing) =>
                            Number(listing.lat) &&
                            Number(listing.lng) && (
                                <React.Fragment key={listing.id}>
                                    {
                                        selectedListing?.id === listing.id && (
                                            <OverlayView
                                                position={{
                                                    lat: Number(listing.lat),
                                                    lng: Number(listing.lng),
                                                }}
                                                mapPaneName={OverlayView.FLOAT_PANE}>
                                                <div className="relative -translate-y-[calc(100%+10px)] -translate-x-1/2" style={{ zIndex: 1001 }}>
                                                    <div className="bg-white rounded-lg shadow-xl border overflow-hidden w-[180px] lg:w-[220px]">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCloseInfoWindow();
                                                            }}
                                                            className="absolute top-1 right-1 z-10 bg-white rounded-full p-1 text-gray-600 hover:text-gray-900 shadow-md hover:shadow-lg transition-all">
                                                            <X size={12} />
                                                        </button>
                                                        <div className="cursor-default">
                                                            {
                                                                listing.medias && listing.medias.length > 0 && (
                                                                    <div className="relative h-20 lg:h-28 bg-gray-100">
                                                                        <img
                                                                            src={`${import.meta.env.VITE_AWS_S3}/${
                                                                                listing.medias[0].path
                                                                            }`}
                                                                            alt={listing.headline}
                                                                            className="w-full h-full object-cover"
                                                                            onError={(e) =>
                                                                                (e.currentTarget.style.display = 'none')
                                                                            }
                                                                        />
                                                                        {isPendingForAgent(listing) && (
                                                                            <div className="absolute top-1 right-1">
                                                                                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 text-[10px] px-1.5 py-0.5">
                                                                                    <Clock size={10} className="mr-1" />
                                                                                    Pending
                                                                                </Badge>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )
                                                            }
                                                            <div className="p-2">
                                                                <div className="text-xs lg:text-sm font-bold text-gray-900 mb-0.5">
                                                                    RM {Helper.formatWithoutDecimal(Number(listing.price))}
                                                                </div>
                                                                <div className="text-xs font-medium text-gray-700 line-clamp-1 mb-0.5">
                                                                    {listing.headline}
                                                                </div>
                                                                <div className="text-[10px] lg:text-xs text-gray-500 line-clamp-1 mb-1">
                                                                    {listing.rawAddress}
                                                                </div>
                                                                {
                                                                    (listing.noOfBedrooms || listing.noOfBathrooms || listing.noOfParkingSpots) && (
                                                                        <div className="flex items-center gap-2 text-gray-600 mb-1 text-[10px]">
                                                                        {listing.noOfBedrooms && (
                                                                            <div className="flex items-center gap-1">
                                                                            <Bed className="w-3 h-3" />
                                                                            <span>{listing.noOfBedrooms}</span>
                                                                            </div>
                                                                        )}
                                                                        {listing.noOfBathrooms && (
                                                                            <div className="flex items-center gap-1">
                                                                            <Bath className="w-3 h-3" />
                                                                            <span>{listing.noOfBathrooms}</span>
                                                                            </div>
                                                                        )}
                                                                        {listing.noOfParkingSpots && (
                                                                            <div className="flex items-center gap-1">
                                                                            <Car className="w-3 h-3" />
                                                                            <span>{listing.noOfParkingSpots}</span>
                                                                            </div>
                                                                        )}
                                                                        </div>
                                                                    )
                                                                }
                                                                {
                                                                    <div className='flex gap-2 items-center mt-3'>
                                                                        <Avatar className="rounded-full">
                                                                            <AvatarImage
                                                                            src={`${import.meta.env.VITE_AWS_S3}/${listing.agent.avatar}`}/>
                                                                            <AvatarFallback>
                                                                                {listing.agent.firstName[0]}
                                                                                {listing.agent.lastName[0]}
                                                                            </AvatarFallback>
                                                                        </Avatar>
                                                                        <div>
                                                                            <p className='text-xs font-semibold text-black'>{listing.agent.firstName} {listing.agent.lastName}</p>
                                                                            <p className='text-xs font-light text-black'>{listing.agent.renNo}</p>
                                                                        </div>
                                                                    </div>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </OverlayView>
                                        )
                                    }
                                </React.Fragment>
                            )
                        )
                    }
                </GoogleMap>
            </div> */}
            <div 
            ref={listViewRef}
            className={`w-full px-1 overflow-y-auto ${
                activeTab === 'list' ? 'block' : 'hidden lg:block'
            }`}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 pb-4">
                    {
                        listings.map((listing) => (
                            <div 
                                key={listing.id}
                                onClick={() => handleCardClick(listing)}
                                className={`bg-white overflow-hidden rounded-lg shadow-md cursor-pointer transition-all hover:shadow-xl ${
                                    selectedListing?.id === listing.id ? 'ring-2 ring-blue-500 shadow-xl' : ''
                                }`}>
                                {
                                    listing.medias && listing.medias.length > 0 && (
                                        <div className="relative h-32 lg:h-36 bg-gray-100">
                                            <img
                                                src={`${import.meta.env.VITE_AWS_S3}/${listing.medias[0].path}`}
                                                alt={listing.headline}
                                                className="w-full h-full object-cover"
                                                onError={(e) => (e.currentTarget.style.display = 'none')}
                                            />
                                            <Badge variant={'default'} className='absolute top-1 left-1'>
                                                {listing.forSaleOrRentId === 1 ? 'SALE' : 'RENT'}
                                            </Badge>
                                            {isPendingForAgent(listing) && (
                                                <Badge variant="secondary" className='absolute top-1 right-1 bg-yellow-100 text-yellow-800'>
                                                    <Clock size={12} className="mr-1" />
                                                    Pending
                                                </Badge>
                                            )}
                                        </div>
                                    )
                                }
                                <div className="p-2.5 lg:p-3">
                                    <div className="text-sm font-bold text-gray-900">
                                        RM {Helper.formatWithoutDecimal(Number(listing.price))}
                                    </div>
                                    <div className="text-xs lg:text-sm font-medium text-gray-700 line-clamp-2">
                                        {listing.headline}
                                    </div>
                                    <div className="text-xs text-gray-500 line-clamp-1">
                                        {listing.rawAddress}
                                    </div>
                                    {
                                        (listing.noOfBedrooms || listing.noOfBathrooms || listing.noOfParkingSpots) && (
                                            <div className="flex items-center gap-2.5 text-xs text-gray-600 mt-2">
                                                {
                                                    listing.noOfBedrooms && (
                                                        <div className="flex items-center gap-1">
                                                            <Bed size={13} />
                                                            <span>{listing.noOfBedrooms}</span>
                                                        </div>
                                                    )
                                                }
                                                {
                                                    listing.noOfBathrooms && (
                                                        <div className="flex items-center gap-1">
                                                            <Bath size={13} />
                                                            <span>{listing.noOfBathrooms}</span>
                                                        </div>
                                                    )
                                                }
                                                {
                                                    listing.noOfParkingSpots && (
                                                        <div className="flex items-center gap-1">
                                                            <Car size={13} />
                                                            <span>{listing.noOfParkingSpots}</span>
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        )
                                    }
                                    <div className="flex gap-2 items-center mt-3">
                                        <Avatar className="rounded-full w-10 h-10">
                                          <AvatarImage
                                            src={`${import.meta.env.VITE_AWS_S3}/${listing.agent.avatar}`}
                                          />
                                          <AvatarFallback>
                                            {listing.agent.firstName[0]}
                                            {listing.agent.lastName[0]}
                                          </AvatarFallback>
                                        </Avatar>
                                      
                                        <div className="flex justify-between items-center w-full">
                                            <div>
                                                <p className="text-xs font-semibold">
                                                {listing.agent.firstName} {listing.agent.lastName}
                                                </p>
                                                <p className="text-xs text-gray-500">{listing.agent.renNo}</p>
                                            </div>
                                      
                                            <Link
                                                to={`/listings/view/${listing.id}`}
                                            >
                                                <Button
                                                variant="default"
                                                size="sm"
                                                className="text-xs"
                                                >
                                                View
                                                </Button>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    }
                </div>
            </div>
        </div>
    )
}

export default CoShareListingTable